/**
 * Keycloak realm roles for the Authmatech dashboard.
 * These match the roles configured in Keycloak → Realm roles.
 * See docs/keycloak-setup.md for the full role hierarchy.
 */
export type DashboardRole =
  | 'AUTHMATECH_SUPER_ADMIN'
  | 'AUTHMATECH_ADMIN'
  | 'AUTHMATECH_OPERATOR'
  | 'AUTHMATECH_SUPPORT'
  | 'AUTHMATECH_FINANCE'
  | 'AUTHMATECH_VIEWER'
  // Client / tenant portal roles — one shared realm, isolated by authmatech_client_id claim
  | 'AUTHMATECH_CLIENT_ADMIN'
  | 'AUTHMATECH_CLIENT_OPERATOR'
  | 'AUTHMATECH_CLIENT_FINANCE'
  | 'AUTHMATECH_CLIENT_VIEWER';

/** Convenience constant for any client portal role check. */
export const ALL_CLIENT_ROLES: DashboardRole[] = [
  'AUTHMATECH_CLIENT_ADMIN',
  'AUTHMATECH_CLIENT_OPERATOR',
  'AUTHMATECH_CLIENT_FINANCE',
  'AUTHMATECH_CLIENT_VIEWER',
];

/** Legacy alias kept for backward compatibility with existing navItems/route guards. */
export type Role = DashboardRole;

export interface DashboardUser {
  id: string;
  email: string;
  name: string;
  preferredUsername: string;
  /** Resolved from realm_access.roles in the Keycloak JWT. */
  roles: DashboardRole[];
  /**
   * Resolved from the custom JWT claim {@code authmatech_client_id} (set via
   * Keycloak user-attribute protocol mapper). Only present for client portal users.
   * See authmatech-identity README for mapper setup.
   */
  clientId?: string;
}

/** Kept for API client compatibility — not used for login (Keycloak handles it). */
export interface LoginRequest {
  email: string;
  password: string;
}

/** Not used with Keycloak — the token comes from the Keycloak redirect flow. */
export interface LoginResponse {
  token: string;
  expiresIn: number;
  user: DashboardUser;
}

/** Backward-compat alias for code that still imports AuthUser. */
export type AuthUser = DashboardUser & { clientId: string; clientName: string };
